import { useState, useEffect } from 'react'
import AnimatedNumber from './AnimatedNumber'
import costsData from '../data/costs.json'

export default function CostPanel() {
  const costs = costsData

  if (!costs) return null

  return (
    <div className="bg-surface rounded-2xl border border-white/[0.06] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-base font-bold text-white">💰 التكاليف التشغيلية</h3>
          <p className="text-[11px] text-[#94A3B8] mt-0.5">آخر تحديث: {costs.lastUpdated}</p>
        </div>
        <div className="text-right">
          <p className="text-xl font-extrabold text-white">
            <AnimatedNumber value={costs.totalMonthlySAR} /> <span className="text-sm text-[#94A3B8]">ريال/شهر</span>
          </p>
          <p className="text-[11px] text-[#94A3B8]">${costs.totalMonthlyUSD}/mo</p>
        </div>
      </div>

      {/* VPS */}
      <div className="mb-4 p-3.5 bg-white/[0.04] rounded-xl border border-white/[0.04]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="text-lg">🖥️</span>
            <div>
              <p className="text-sm font-semibold text-white">VPS — {costs.vps.provider}</p>
              <p className="text-[11px] text-[#94A3B8]">{costs.vps.specs}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-white"><AnimatedNumber value={costs.vps.monthlySAR} /> ريال</p>
            <p className="text-[11px] text-[#94A3B8]">${costs.vps.monthlyUSD}/mo</p>
          </div>
        </div>
      </div>

      {/* Token Usage by Model */}
      <div className="mb-4">
        <p className="text-[11px] font-semibold text-[#94A3B8] tracking-wider uppercase mb-3">استهلاك التوكنات حسب الموديل</p>
        <div className="space-y-2.5">
          {(costs.modelBreakdown || []).map((m, i) => (
            <div key={i} className="p-3 bg-white/[0.03] rounded-xl border border-white/[0.03]">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-semibold text-white">{m.model}</p>
                <p className="text-sm font-bold text-white">{(m.tokens / 1000000).toFixed(1)}M توكن</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-[#94A3B8]">{m.agents}</p>
                <p className="text-[11px] font-semibold" style={{ color: m.costSAR === 0 ? '#4CAF50' : '#F59E0B' }}>
                  {m.costSAR === 0 ? 'مشمول بالاشتراك' : `${m.costSAR} ريال`}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Token Projections */}
      {costs.projections && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold text-[#94A3B8] tracking-wider uppercase mb-3">الاستهلاك المتوقع</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: 'يومي', data: costs.projections.daily, icon: '📅' },
              { label: 'أسبوعي', data: costs.projections.weekly, icon: '📆' },
              { label: 'شهري', data: costs.projections.monthly, icon: '🗓️' },
              { label: 'سنوي', data: costs.projections.yearly, icon: '📊' },
            ].map((p, i) => (
              <div key={i} className="p-3 bg-white/[0.03] rounded-xl border border-white/[0.03] text-center">
                <p className="text-[10px] text-[#94A3B8] mb-1">{p.icon} {p.label}</p>
                <p className="text-sm font-bold text-white">{(p.data.tokens / 1000000).toFixed(1)}M</p>
                <p className="text-[11px] text-[#94A3B8]">{p.data.costSAR} ريال</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost per Product */}
      <div className="mb-4">
        <p className="text-[11px] font-semibold text-[#94A3B8] tracking-wider uppercase mb-3">تكلفة كل منتج</p>
        <div className="grid grid-cols-2 gap-2">
          {(costs.breakdown || []).map((b, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 bg-white/[0.03] rounded-lg border border-white/[0.03]">
              <span className="text-[11px] text-white font-medium">{b.item || b.product}</span>
              <span className="text-[11px] font-bold text-white">{b.monthlySAR === 0 ? 'مجاني' : `${b.monthlySAR} ريال`}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Alert if subscription critical */}
      {costs.subscriptionAlert?.status === 'CRITICAL' && (
        <div className="p-3.5 bg-red-500/[0.08] rounded-xl border border-red-500/[0.15] mb-4">
          <p className="text-[11px] text-red-400 font-medium text-center">
            ⚠️ {costs.subscriptionAlert.message}
          </p>
        </div>
      )}

      {/* Highlight */}
      <div className="p-3.5 bg-[#8B5CF6]/[0.08] rounded-xl border border-[#8B5CF6]/[0.15]">
        <p className="text-[11px] text-white font-medium text-center">
          🦅 تشغيل شركة SaaS كاملة بـ <strong className="text-[#C084FC]">{costs.totalMonthlySAR} ريال/شهر</strong> — صفر موظفين
        </p>
      </div>
    </div>
  )
}
